import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Filter, X, CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PropTypes from "prop-types";

const FilterDropdown = ({ onFiltersChange, activeFilterStatus }) => {
  const [activeFilters, setActiveFilters] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
  });
  const [isOpen, setIsOpen] = useState(false);

  const sortOptions = [
    { id: "sort-az", label: "Sort by A-Z", type: "sort", value: "name-asc" },
    { id: "sort-za", label: "Sort by Z-A", type: "sort", value: "name-desc" },
  ];

  const durationOptions = [
    { id: "duration-8", label: "8 mins", type: "duration", value: 8 },
    { id: "duration-30", label: "30 mins", type: "duration", value: 30 },
    { id: "duration-45", label: "45 mins", type: "duration", value: 45 },
    { id: "duration-60", label: "60 mins", type: "duration", value: 60 },
  ];

  // When activeFilterStatus changes and it's not "completed", remove duration filters
  useEffect(() => {
    if (activeFilterStatus !== "Completed") {
      setActiveFilters(prev => prev.filter(filter => filter.type !== "duration"));
    }
  }, [activeFilterStatus]);

  const handleCheckboxChange = (option, checked) => {
    if (checked) {
      if (option.type === "sort") {
        setActiveFilters((prev) => [
          ...prev.filter((f) => f.type !== "sort"),
          option,
        ]);
      } else {
        setActiveFilters((prev) => [...prev, option]);
      }
    } else {
      setActiveFilters((prev) =>
        prev.filter((filter) => filter.id !== option.id)
      );
    }
  };

  // Handle date range selection (only for display, not sent yet)
  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      const dateRangeFilter = {
        id: "date-range",
        label: `${format(dateRange.from, "MMM d")} - ${format(
          dateRange.to,
          "MMM d"
        )}`,
        type: "dateRange",
        value: dateRange,
      };

      setActiveFilters((prev) => [
        ...prev.filter((f) => f.type !== "dateRange"),
        dateRangeFilter,
      ]);
    }
  }, [dateRange]);

  // Apply filters when "Apply Filters" is clicked
  const applyFilters = () => {
    const sortFilter = activeFilters.find((f) => f.type === "sort");
    const durationFilters = activeFilters.filter((f) => f.type === "duration");
    const dateRangeFilter = activeFilters.find((f) => f.type === "dateRange");

    const filters = {
      sortBy: sortFilter ? sortFilter.value : "newest", // Default to "newest" if no sort selected
      durations:
        durationFilters.length > 0
          ? durationFilters.map((f) => f.value).join(",")
          : "",
      startDate: dateRangeFilter
        ? format(dateRangeFilter.value.from, "yyyy-MM-dd")
        : undefined,
      endDate: dateRangeFilter
        ? format(dateRangeFilter.value.to, "yyyy-MM-dd")
        : undefined,
    };

    onFiltersChange(filters); // Send filters to parent
    setIsOpen(false); // Close the popover
  };

  // Clear all filters and send reset request
  const clearAllFilters = () => {
    setActiveFilters([]);
    setDateRange({ from: undefined, to: undefined });
    onFiltersChange({
      sortBy: "newest",
      durations: "",
      startDate: undefined,
      endDate: undefined,
    }); // Send reset filters to parent
  };

  // Remove a filter
  const removeFilter = (filterId) => {
    if (filterId === "date-range") {
      setDateRange({ from: undefined, to: undefined });
    }
    setActiveFilters((prev) => prev.filter((filter) => filter.id !== filterId));
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full bg-pink-500 text-white hover:bg-pink-600 hover:text-white"
            aria-label="Filter options"
          >
            <Filter className="h-5 w-5" />
            {activeFilters.length > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                {activeFilters.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="end">
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="flex items-center justify-between sticky top-0 bg-white z-10 pb-2">
              <h3 className="font-medium text-sm">Filters</h3>
              {activeFilters.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </Button>
              )}
            </div>

            {/* Active filters */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1 pb-2 border-b">
                {activeFilters.map((filter) => (
                  <div
                    key={filter.id}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100"
                  >
                    <span>{filter.label}</span>
                    <button
                      onClick={() => removeFilter(filter.id)}
                      className="ml-1 text-gray-500 hover:text-gray-700"
                      aria-label={`Remove ${filter.label} filter`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Sort options */}
            <div className="space-y-1.5">
              <h4 className="text-xs font-medium">Sort</h4>
              <div className="space-y-1.5">
                {sortOptions.map((option) => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={option.id}
                      checked={activeFilters.some((f) => f.id === option.id)}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(option, checked)
                      }
                    />
                    <label
                      htmlFor={option.id}
                      className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Date Range Picker */}
            <div className="space-y-1.5">
              <h4 className="text-xs font-medium">Date Range</h4>
              <div className="flex flex-col space-y-2">
                <div className="flex flex-col gap-2">
                  {/* Start Date (Forward) */}
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs justify-start text-left font-normal h-8 w-full"
                        >
                          <CalendarIcon className="mr-2 h-3 w-3" />
                          {dateRange.from ? (
                            format(dateRange.from, "PPP")
                          ) : (
                            <span>Start date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={dateRange.from}
                          onSelect={(date) =>
                            setDateRange((prev) => ({ ...prev, from: date }))
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Separator */}
                  <div className="text-center text-xs text-gray-500">
                    — to —
                  </div>

                  {/* End Date (Backward) */}
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs justify-start text-left font-normal h-8 w-full"
                        >
                          <CalendarIcon className="mr-2 h-3 w-3" />
                          {dateRange.to ? (
                            format(dateRange.to, "PPP")
                          ) : (
                            <span>End date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={dateRange.to}
                          onSelect={(date) =>
                            setDateRange((prev) => ({ ...prev, to: date }))
                          }
                          initialFocus
                          disabled={(date) =>
                            dateRange.from ? date < dateRange.from : false
                          }
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </div>

            {/* Duration options - Only show when activeFilterStatus is "Completed" */}
            {activeFilterStatus === "Completed" && (
              <div className="space-y-1.5">
                <h4 className="text-xs font-medium">Duration</h4>
                <div className="grid grid-cols-2 gap-1.5">
                  {durationOptions.map((option) => (
                    <div key={option.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={option.id}
                        checked={activeFilters.some((f) => f.id === option.id)}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(option, checked)
                        }
                      />
                      <label
                        htmlFor={option.id}
                        className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Apply button */}
            <Button
              className="w-full text-sm h-8 mt-2"
              onClick={applyFilters}
            >
              Apply Filters
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

FilterDropdown.propTypes = {
  onFiltersChange: PropTypes.func.isRequired,
  activeFilterStatus: PropTypes.string.isRequired,
};

export default FilterDropdown;